import type { FlowDoc, FlowStep } from '../types/global';
import { createAltYAgent, deleteAltYAgent, listAltYAgents, updateAltYAgent } from './altYApi';

export type PinnedAgent = {
  id: string;
  label: string;
  icon: string;
  // Mode the chat enters when the pin is activated. Runnable pins always run
  // in `interact` (the only mode with page-acting tools); diagram-only pins
  // can sit in any mode. `bmad` runs the real BMAD-METHOD lifecycle.
  mode: 'interact' | 'alty-dev' | 'agent-factory' | 'online-dev-mvp' | 'bmad' | 'chat';
  // When true, clicking the pin opens a fresh interact-mode chat and primes
  // the first user message with the flow as instructions for the agent. When
  // false, the pin is purely cosmetic / diagram-only (the built-in "interact"
  // pin, which describes default Alt-Y behaviour).
  runnable: boolean;
  flow: FlowDoc;
  // Run ID of the agent-factory chat that produced this agent's diagram.
  // Used so clicking the pin from Agent Factory re-enters that conversation
  // (instead of starting a new chat) and lets the user continue editing the
  // diagram. Absent on built-ins and on agents pinned before this field existed.
  creatorRunId?: number;
  // True (or absent) → shown in the sidebar pin list. False → registered in
  // the AgentPicker dropdown only. "Make agent" creates with pinned=false;
  // the diagram's "Pin" button flips it to true via PUT /api/alty/agents/<id>.
  pinned?: boolean;
  // Broker-side runner fields. The runner (`onesheet-agent-y`) ticks at
  // `schedule` rate when `enabled` is true; `last_run_*` reflects the most
  // recent tick. Toggle via PUT /api/alty/agents/<id> with {enabled}.
  enabled?: boolean;
  schedule?: 'on_demand' | 'every_10s' | 'every_1m' | 'every_10m' | 'every_1h';
  last_run_at?: number | null;
  last_run_status?: 'ok' | 'error' | 'skipped' | null;
  last_run_message?: string | null;
  // Customer + deployment binding. Captured from bridge context at Make-agent
  // time so the scheduled runner can act on the right customer/host.
  customerId?: number;
  baseUrl?: string;
  // Tick cadence (seconds) and per-tick max runtime (seconds). The broker
  // infers these from the trigger step text too; storing them here makes them
  // editable via the Recent-ticks panel without re-creating the agent.
  tickIntervalSeconds?: number;
  tickMaxRuntimeSeconds?: number;
  // Broker-managed history of recent ticks. Present on GET responses.
  tickHistory?: Array<{ at: number; status: 'ok' | 'error' | 'skipped'; message: string }>;
};

// Minimal shape per AGENT_FACTORY_PROMPT — title + a short spine of plain
// steps, no chips/gates/forks/loops/eyebrow/description. Describes what the
// `interact` mode actually does today; edit in lockstep if implementation drifts.
const onlineInteractionFlow: FlowDoc = {
  title: 'Online Interaction',
  steps: [
    { kind: 'trigger', text: 'User opens Alt-Y on a OneSheet page' },
    { kind: 'step', text: 'Panel reads the current page context (URL, customer, document, selection)' },
    { kind: 'step', text: 'User types a request in the composer' },
    { kind: 'step', text: 'Agent answers or acts on the page via AgentProxy / MCP tools' },
    { kind: 'step', text: 'Reply streams back into the chat' },
  ],
};

// Self-portrait of agent-factory mode — minimal shape per AGENT_FACTORY_PROMPT:
// title + a short spine of plain steps, no chips/gates/forks/loops.
// Non-runnable like the `interact` pin — it describes the mode, not a job.
const agentFactoryFlow: FlowDoc = {
  title: 'Agent Factory',
  description: 'Designs agents as diagrams with a concise brief, inferred details, and implementation-ready requirements.',
  steps: [
    { kind: 'trigger', text: 'User describes the workflow or agent they want in the composer' },
    { kind: 'step', text: 'Agent sketches the smallest possible diagram — a title and 3–5 plain steps' },
    { kind: 'step', text: 'Agent also writes a brief: short description, original request, clarified rewrite, and detailed spec with inferred bits in blue' },
    { kind: 'step', text: 'Diagram + brief are emitted as JSON in a fenced code block; the panel renders the diagram, with the brief one button-tap away' },
    { kind: 'step', text: 'User asks for tweaks; agent re-emits the whole updated JSON until the user is happy' },
  ],
};

// Self-portrait of `chat` mode — the simplest built-in: a general-purpose
// helpful assistant with no fixed goal. Non-runnable like the `interact` and
// `agent-factory` pins (it describes a mode, not a job). The real framing lives
// in the broker's CHAT_PROMPT; keep this minimal sketch in sync with it.
const chatFlow: FlowDoc = {
  title: 'Chat',
  description:
    'A general-purpose helpful assistant with no fixed goal — able to use any Alt-Y agent capability from the same conversation.',
  steps: [
    { kind: 'trigger', text: 'User opens Alt-Y and types anything — a question, an idea, a request' },
    { kind: 'step', text: 'Agent helps directly, choosing the right Alt-Y capability for this turn' },
    { kind: 'step', text: 'It can read the entire memory store and recall anything the fleet has learned' },
    { kind: 'step', text: 'When the task needs it, it acts on the page or dispatches the right build agent in the same chat' },
  ],
};

// Goal Discovery — a customized product-discovery flow (in the spirit of BMAD).
// An executive describes a goal in their own words; the agent gathers only the
// context it needs, rephrases the goal, and produces four pieces (filled-in
// gaps, key questions, a step-by-step user story, and design ideas). It runs in
// `interact` (full page + research tools) and emits an { alty_discovery: {...} }
// JSON block the panel renders as tabs on the right (DiscoveryCanvas). The heavy
// operational spec rides in DISCOVERY_AGENT_PRIMER (appended in
// formatFlowAsPrimer), so it travels with every invocation. Recognised by id
// ('discovery') via isDiscoveryAgent.
const discoveryFlow: FlowDoc = {
  title: 'Goal Discovery',
  description:
    "Turns an executive's stream-of-consciousness goal into a clear brief: a rephrase plus four pieces — filled-in gaps, key questions, a step-by-step user story, and design ideas — each in its own tab.",
  steps: [
    { kind: 'trigger', text: 'An executive describes a goal — a feature, a bug, a new setup — in their own words' },
    { kind: 'step', text: 'Agent gathers only the context it needs (related work, the codebase) — or none' },
    { kind: 'step', text: 'Agent rephrases the goal clearly and shows it immediately' },
    { kind: 'step', text: 'It fills the most important gaps, asks the key questions, drafts a step-by-step user story, and sketches design ideas' },
    { kind: 'step', text: 'All four pieces appear as tabs on the right; the user iterates until happy' },
  ],
};

const altyDevFlow: FlowDoc = {
  title: 'Y-bar Dev',
  steps: [
    { kind: 'trigger', text: 'User requests a feature or bugfix for the Alt-Y panel or shell' },
    { kind: 'step', text: 'Agent researches the React app, host paths, and CLAUDE.md conventions' },
    { kind: 'step', text: 'Agent edits React/TypeScript code within its dedicated git worktree' },
    { kind: 'step', text: 'System runs "npm run build" to verify types and generate the bundle' },
    { kind: 'step', text: 'Orchestrator atomic-swaps the bundle into the live targets' },
  ],
};

// Runnable BMAD-METHOD lifecycle mode. The `bmad` AltYMode dispatches a real
// claude turn (full filesystem + onesheet server access, same as interact)
// primed to drive the installed BMAD-METHOD plugin skills phase by phase —
// Analyst → PM → Architect → Scrum Master → Developer → QA. Each phase invokes
// the matching bmad-* skill (bmad-product-brief / bmad-agent-analyst →
// bmad-create-prd → bmad-create-architecture → bmad-create-epics-and-stories →
// bmad-dev-story → bmad-code-review) and persists its artifact under the chat's
// own workspace `_bmad/` dir, which the next phase consumes. See the broker's
// BMAD_PROMPT and docs/bmad.md for the runnable spec.
const bmadFlow: FlowDoc = {
  title: 'BMAD',
  description:
    "Runs the BMAD-METHOD SDLC lifecycle: each phase invokes its bmad-* skill and writes an artifact the next phase reads.",
  steps: [
    { kind: 'trigger', text: 'User has a feature, bug, story, or new product idea — could be standalone or part of an existing project' },
    { kind: 'step', text: 'Triage: AI assesses scope clarity + blast radius; routes to one of Easy fix / Ready story / Needs input / Full lifecycle' },
    { kind: 'step', text: 'Needs input: AI moves the dev-task to FEEDBACK with specific open questions; waits for answers' },
    { kind: 'step', text: 'Easy fix: bmad-quick-dev implements directly; no brief/PRD/architecture artifacts produced' },
    { kind: 'step', text: 'Ready story: bmad-create-story → bmad-dev-story → bmad-code-review (one story against existing architecture)' },
    { kind: 'step', text: 'Full lifecycle (new product/architecture): bmad-product-brief → bmad-create-prd → bmad-create-architecture → bmad-create-epics-and-stories → bmad-dev-story loop → bmad-code-review' },
    { kind: 'step', text: 'Project-level execution: when run against an IDsys Project 508, solve all its Development Tasks (in dep order) before moving on' },
  ],
};

// New OneSheet-backed dev agent: end-to-end runner for `os-*` tasks (task
// Documents in customer 195). Walks `open → in_progress → in_testing → done`
// via the dashboard's `/api/tasks/<id>/set-status` and writes lessons learned
// to memory after each ship. Pinned in Alt-Y so users can launch a task
// directly from the panel or watch ongoing autostart runs.
const onesheetDevFlow: FlowDoc = {
  title: 'OneSheet Dev Agent',
  description:
    "Picks `os-*` tasks from customer 195, implements them, and walks the status lifecycle via the dashboard.",
  steps: [
    { kind: 'trigger', text: 'Dashboard autostart picks an open OneSheet task or user manually invokes it' },
    { kind: 'step', text: 'Set status to in_progress; analyze; estimate confidence + difficulty' },
    { kind: 'step', text: 'Implement in the worktree (online21 ai branch, alty-app main, onesheet-agent-y master)' },
    { kind: 'step', text: 'Produce proof screenshots; verify the change works on ai.idsys.ee' },
    { kind: 'step', text: 'Ship via /ship-onesheet: commit, push, set status to in_testing, add tag' },
    { kind: 'step', text: 'On user approval, set status to done; tear down task runtime; save lessons learned' },
  ],
};

// Meta-agent: builds and validates new agents. Takes a description + 5
// increasingly hard test specs, drafts a flow diagram + impl-doc, runs each
// test against the impl, iterates until all 5 pass. Outputs a finished
// pin-shaped definition that can be saved as a user_agent.
const metaAgentFlow: FlowDoc = {
  title: 'Meta-Agent',
  description:
    "Creates new agents: takes a description + 5 test specs, drafts diagram + impl-doc, runs tests, iterates until all pass.",
  steps: [
    { kind: 'trigger', text: 'User describes the agent they want and 5 increasingly hard test specs' },
    { kind: 'step', text: 'Draft flow diagram (title + 4-6 steps) and impl-doc (markdown spec)' },
    { kind: 'step', text: 'Execute each test against the impl-doc spec; record pass/fail + measured evidence' },
    { kind: 'step', text: 'On failure, revise diagram or impl-doc and re-run the failing test (up to 3 iterations)' },
    { kind: 'step', text: 'When all 5 tests pass, save the agent as a user_agent on the broker + emit the diagram' },
  ],
};

// Documents how OneSheet (the online21 ASP.NET MVC app itself) is developed
// on this box — the C#/Razor/JS edit → dev-instance build → testing.idsys.ee
// verify → master/live tag spine. Non-runnable; there is no `online-dev`
// AltYMode behind it yet, so `mode` stays `interact` and the pin sits idle.
const onlineDevFlow: FlowDoc = {
  title: 'Online Developer',
  description:
    "Develops OneSheet (/opt/online21) on the testing branch; verify, merge to master, tag for live.",
  steps: [
    { kind: 'trigger', text: 'User describes a OneSheet feature, bug, or schema change' },
    { kind: 'step', text: 'Edit C# / Razor / JS on the testing branch in /opt/online21' },
    { kind: 'step', text: 'Build (for backend changes) and verify on a dev.sh per-branch URL' },
    { kind: 'step', text: 'Commit + push to testing; user verifies on testing.idsys.ee' },
    { kind: 'step', text: 'Merge testing → master, then tag master for live deploy' },
  ],
};

// Online Dev MVP — each chat owns a dedicated online21 dev fork. The first
// turn spins it up via /opt/dev.sh; subsequent turns reuse the same fork. The
// left side of the screen iframes the live dev URL so the user can verify
// changes in-place. A Ship It button on the iframe toolbar commits + pushes
// the fork to the upstream `testing` branch.
const onlineDevMvpFlow: FlowDoc = {
  title: 'Online Dev MVP',
  description:
    "Each chat spins up its own online21 dev fork; iframe it on the left; Ship It pushes to testing.",
  steps: [
    { kind: 'trigger', text: 'User describes an online21 change in the composer' },
    { kind: 'step', text: 'Broker runs `/opt/dev.sh start mvp-<runId>` and emits the live URL' },
    { kind: 'step', text: 'Builder claude implements the change inside that dev fork' },
    { kind: 'step', text: 'Iframe on the left shows the live dev env for verification' },
    { kind: 'step', text: 'Ship It commits and pushes the fork to the testing branch' },
  ],
};

export const PINNED_AGENTS: PinnedAgent[] = [
  {
    id: 'interact',
    label: 'Online Interaction',
    icon: 'fa-comments',
    mode: 'interact',
    runnable: false,
    flow: onlineInteractionFlow,
  },
  {
    id: 'chat',
    label: 'Chat',
    icon: 'fa-comment-dots',
    // General-purpose helpful assistant. Non-runnable like `interact` —
    // selecting the pin switches the chat into `chat` mode (workspace-
    // compatible, so it can flip in place mid-conversation); the broker's
    // CHAT_PROMPT supplies all the framing. No flow primer is imposed, so the
    // agent stays goal-free.
    mode: 'chat',
    runnable: false,
    flow: chatFlow,
  },
  // online-dev pin hidden by user preference. Flow def (`onlineDevFlow`) and
  // its impl-doc remain in tree so it can be re-pinned by uncommenting:
  // {
  //   id: 'online-dev',
  //   label: 'online dev',
  //   icon: 'fa-code',
  //   mode: 'interact',
  //   runnable: false,
  //   flow: onlineDevFlow,
  // },
  {
    id: 'discovery',
    label: 'Goal Discovery',
    // fa-bullseye — the agent turns a goal (a target) into a clear brief.
    icon: 'fa-bullseye',
    // Runs in `interact` (the only mode with the page + research tools the
    // discovery agent needs). Selecting it primes the discovery operational
    // spec via formatFlowAsPrimer (DISCOVERY_AGENT_PRIMER); the agent emits an
    // { alty_discovery: {...} } JSON block the panel renders as right-pane tabs
    // (DiscoveryCanvas). Recognised by id via isDiscoveryAgent.
    mode: 'interact',
    runnable: true,
    flow: discoveryFlow,
  },
  {
    id: 'agent-factory',
    label: 'Agent Factory',
    icon: 'fa-sitemap',
    mode: 'agent-factory',
    runnable: false,
    flow: agentFactoryFlow,
  },
  {
    id: 'alty-dev',
    label: 'Y-bar Dev',
    icon: 'fa-laptop-code',
    mode: 'alty-dev',
    runnable: false,
    flow: altyDevFlow,
  },
  {
    id: 'online-dev-mvp',
    label: 'Online Dev MVP',
    icon: 'fa-globe',
    mode: 'online-dev-mvp',
    runnable: false,
    flow: onlineDevMvpFlow,
  },
  {
    id: 'bmad',
    label: 'BMAD',
    // fa-people-arrows / fa-people-group aren't in this FA version on the
    // Alt-Y app (only fa-people-carry is present); use fa-people-carry as
    // the role-hand-off metaphor. Verified via
    // /opt/alty-app/public/lib/fontawesome/css/all.min.css.
    icon: 'fa-people-carry',
    // Real runnable BMAD-METHOD lifecycle. Clicking the pin opens a fresh
    // `bmad`-mode chat and primes it with bmadFlow; the broker's bmad handler
    // drives the installed bmad-* plugin skills phase by phase.
    mode: 'bmad',
    runnable: true,
    flow: bmadFlow,
  },
  // The `onesheet-dev` and `meta-agent` pins were shipped from the autonomous
  // mega-run and then taken back out of the sidebar by user preference. The
  // flow defs (`onesheetDevFlow`, `metaAgentFlow`) and their impl-docs under
  // /opt/onesheet-agent-y/docs/{onesheet-dev,meta-agent}.md remain in tree so
  // they can be re-pinned by uncommenting these entries below.
  // {
  //   id: 'onesheet-dev',
  //   label: 'OneSheet Dev',
  //   icon: 'fa-robot',
  //   mode: 'interact',
  //   runnable: false,
  //   flow: onesheetDevFlow,
  // },
  // {
  //   id: 'meta-agent',
  //   label: 'Meta Agent',
  //   icon: 'fa-wand-magic-sparkles',
  //   mode: 'agent-factory',
  //   runnable: false,
  //   flow: metaAgentFlow,
  // },
];

// Pseudo-agent for AUTO routing. Kept OUT of PINNED_AGENTS so checks like
// `agent.id !== 'interact'` and pin iteration loops don't accidentally pick
// it up; the picker prepends it explicitly. Its `flow` is a placeholder so
// the FlowDoc-typed surface stays happy — pickAutoAgent never reads it.
export const AUTO_AGENT: PinnedAgent = {
  id: 'auto',
  label: 'AUTO',
  // fa-compass — AUTO finds the right direction / routes to the best agent.
  icon: 'fa-compass',
  mode: 'interact',
  runnable: true,
  flow: { title: 'AUTO', steps: [] },
};

// Short, hand-written descriptions of the three built-in modes, used by the
// AUTO router as the "haystack" alongside each candidate's label. Kept here
// (not in PINNED_AGENTS) so each pin's `flow.description` stays free to
// describe the diagram itself rather than routing intent.
const BUILTIN_DESCRIPTIONS: Record<string, string> = {
  interact:
    "Default. Work with the customer's data in OneSheet — query, create, update records via AgentProxy / MCP tools. Use this for anything about Documents, DataRows, Persons, Articles.",
  chat:
    "General-purpose helpful assistant with no fixed goal. Answers questions, explains things, brainstorms, drafts text, and can use any Alt-Y agent capability in the same conversation, including build dispatch to Y-bar Dev or Online Dev MVP. Use for open-ended help, explanations, or mixed work that may change from turn to turn.",
  discovery:
    "Turn a high-level goal or feature idea into a structured discovery brief: a clear rephrase plus filled-in gaps, the key clarifying questions, a step-by-step user story, and design ideas — each in its own tab. Use when the user describes WHAT they want built and wants it organized into a spec before any code.",
  'agent-factory':
    'Design a multi-step workflow with multiple roles/sub-agents and emit it as a flow diagram. Use when the user wants to BUILD a new agent or describe a process.',
  'alty-dev':
    "Develop the Alt-Y panel app itself (React/TypeScript code in /opt/alty-app). Use when the user wants to change Alt-Y's own UI/behavior.",
  'online-dev-mvp':
    "Develop OneSheet (the online21 ASP.NET MVC app) inside a chat-scoped dev fork. Use when the user wants to change OneSheet itself and verify on a live URL before shipping to testing.",
  bmad:
    'Run the real BMAD-METHOD SDLC lifecycle (Analyst → PM → Architect → SM → Developer → QA) end-to-end from idea to shipped feature, driving the installed bmad-* skills and persisting brief / PRD / architecture / stories / code review as artifacts. Use when the user wants the structured multi-role hand-off instead of a single agent — PRD, spec, architecture, story, epic words land here.',
};

const STOPWORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'have', 'i', 'in', 'into', 'is', 'it', 'its', 'me', 'my',
  'of', 'on', 'or', 'that', 'the', 'their', 'them', 'then', 'this',
  'to', 'was', 'we', 'were', 'will', 'with', 'you', 'your', 'do',
  'can', 'how', 'what', 'when', 'where', 'why', 'which', 'who',
  'about', 'just', 'please', 'pls', 'now', 'so', 'also',
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter((w) => w.length >= 3 && !STOPWORDS.has(w));
}

// Public version of haystackForCandidate that callers (AUTO router) can use
// to ship a clean description string to the broker. Falls back to the flow's
// own step text for user-pins so they're routable without hand-written copy.
export function describeAgentForRouting(agent: PinnedAgent): string {
  return haystackForCandidate(agent);
}

function haystackForCandidate(candidate: PinnedAgent): string {
  const builtin = BUILTIN_DESCRIPTIONS[candidate.id];
  if (builtin) return `${candidate.label} ${builtin}`;
  const flow = candidate.flow;
  const stepText = (flow?.steps || [])
    .map((s) => s.text || '')
    .join(' ')
    .slice(0, 200);
  return `${candidate.label} ${flow?.title || ''} ${flow?.description || ''} ${stepText}`;
}

function scoreCandidate(promptTokens: string[], candidate: PinnedAgent): number {
  if (!promptTokens.length) return 0;
  const hay = haystackForCandidate(candidate).toLowerCase();
  const hayTokens = new Set(tokenize(hay));
  let score = 0;
  for (const token of promptTokens) {
    if (hayTokens.has(token)) score += 2;
    else if (hay.includes(token)) score += 1;
  }
  return score;
}

// Heuristic router: pick the candidate whose label + description shares the
// most keywords with the prompt. Falls back to `interact` when nothing scores
// above zero — interact is the only mode with the page tools, so silently
// routing an ambiguous prompt to alty-dev / agent-factory would surprise the
// user far more than a default-mode reply that misses context.
export function pickAutoAgent(prompt: string, candidates: PinnedAgent[]): PinnedAgent {
  const fallback = candidates.find((c) => c.id === 'interact') ?? candidates[0];
  if (!candidates.length) return AUTO_AGENT;
  const tokens = tokenize(prompt);
  if (!tokens.length) return fallback;
  let best: PinnedAgent | null = null;
  let bestScore = 0;
  for (const candidate of candidates) {
    const score = scoreCandidate(tokens, candidate);
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best ?? fallback;
}

const userAgentsKey = 'altY.userAgents.v1';

// Built-in pins (PINNED_AGENTS) don't live in localStorage, so we keep a
// separate map of `pinId → editor chat runId` here. Used so re-clicking the
// Agent Factory pin reattaches to the existing "edit the factory" chat
// instead of minting a new one every time. See docs/agent-factory.md for the
// end-to-end flow.
// v2: bumped to discard bindings polluted by a switchMode-auto-attach race
// that mis-bound editable built-ins (interact/alty-dev) to whatever
// agent-factory chat was last open. Safe to bump — losing a binding just
// means re-click the pin to associate a fresh edit chat.
const builtinEditChatKey = 'altY.builtinEditChat.v2';

function readBuiltinEditMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(builtinEditChatKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function getBuiltinEditChatId(pinId: string): number | null {
  return readBuiltinEditMap()[pinId] ?? null;
}

export function setBuiltinEditChatId(pinId: string, runId: number | null): void {
  const map = readBuiltinEditMap();
  if (runId == null) delete map[pinId];
  else map[pinId] = runId;
  try {
    localStorage.setItem(builtinEditChatKey, JSON.stringify(map));
  } catch {
    /* quota or disabled storage — ignore */
  }
}

// Which built-in pins the user has chosen to show in the sidebar. Built-ins
// are NOT pinned by default — the user pins each one on demand via the "Pin"
// button above its diagram. Kept client-side because built-ins live in code
// (PINNED_AGENTS), not on the broker like user agents.
const pinnedBuiltinsKey = 'altY.pinnedBuiltins.v1';

export function getPinnedBuiltinIds(): Set<string> {
  try {
    const raw = localStorage.getItem(pinnedBuiltinsKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

export function setBuiltinPinned(id: string, pinned: boolean): void {
  const ids = getPinnedBuiltinIds();
  if (pinned) ids.add(id);
  else ids.delete(id);
  try {
    localStorage.setItem(pinnedBuiltinsKey, JSON.stringify([...ids]));
  } catch {
    /* quota or disabled storage — ignore */
  }
}

// Built-in pins that can be edited via the "Edit agent" button. Lists the
// pin ids whose static `flow` is the source of truth for the corresponding
// broker behaviour (currently just agent-factory). New entries here need a
// matching `pin_id != "agent-factory"` branch in `edit_pinned_agent` in
// /opt/onesheet-agent-y/main.py and a doc in /opt/onesheet-agent-y/docs/.
export const EDITABLE_BUILTIN_PIN_IDS = new Set([
  'agent-factory',
  'interact',
  'chat',
  'alty-dev',
  // Documentation-only pins; clicking the pen opens an agent-factory editor
  // chat so the user can iterate the diagram. Pressing "Edit agent" dispatches
  // to the broker, which writes per-pin impl docs under
  // /opt/onesheet-agent-y/docs/<pin>.md — first edit creates the doc.
  'bmad',
  'online-dev-mvp',
  // onesheet-dev + meta-agent removed from the sidebar but their impl-docs
  // remain in /opt/onesheet-agent-y/docs/. Re-add the ids here if those pins
  // come back.
]);

// The Idea Agent is a user-pin whose single job is to jot feature ideas into a
// per-department list (see the broker's agent_ideas_* store + /api/alty/ideas).
// It has no dedicated mode/id, so we recognise it by its title — there's one
// such special agent. When the selected agent is an Idea Agent the panel
// captures each sent message as an idea and offers the ideas-list toggle.
export function isIdeaAgent(agent: PinnedAgent | null | undefined): boolean {
  if (!agent) return false;
  const name = (agent.label || agent.flow?.title || '').trim().toLowerCase();
  return name === 'idea agent';
}

// The "Recent Apartment Origin Finder" is a user-pin whose job is to search the
// Dutch rental market, trace each repost back to its original agent listing, and
// save verified result cards (see the broker's agent_apartments_* store +
// /api/alty/apartments). Like the Idea Agent it has no dedicated mode/id, so we
// recognise it by title — any agent whose name mentions "apartment". When it's
// the active selection the panel parses the apartments the agent emits, persists
// them, and offers the apartments-list toggle.
export function isApartmentAgent(agent: PinnedAgent | null | undefined): boolean {
  if (!agent) return false;
  const name = (agent.label || agent.flow?.title || '').trim().toLowerCase();
  return name.includes('apartment');
}

// The "Customized BMad Agent Flow" is a user-pin that runs a BMad-style intake:
// an executive states a goal, the agent gathers bounded context, rephrases it,
// and drafts focused pieces (gaps, clarification questions, user story, design
// ideas, major architecture decisions) shown in the right-side tabs. These pins
// are recognised by stable server-side user-agent ids so their visible labels
// can change without losing the BMAD workflow UI.
const BMAD_WORKFLOW_AGENT_IDS = new Set([
  'user-1779714618202-qfo2e',
  'user-1779866538323-ndu20',
]);

export function isBmadWorkflowAgent(agent: PinnedAgent | null | undefined): boolean {
  if (!agent) return false;
  if (agent.mode === 'bmad') return false;
  return BMAD_WORKFLOW_AGENT_IDS.has(agent.id);
}

// The Goal Discovery agent is a built-in pin (id 'discovery') whose job is to
// turn an executive's stream-of-consciousness goal into a structured brief —
// rephrase + four pieces, rendered as right-pane tabs (DiscoveryCanvas). It runs
// in `interact` mode like any pin; the panel recognises it by id so it can parse
// the { alty_discovery: {...} } block the agent emits and show the canvas. A
// user copy that keeps the name is recognised too.
export function isDiscoveryAgent(agent: PinnedAgent | null | undefined): boolean {
  if (!agent) return false;
  if (agent.id === 'discovery') return true;
  const name = (agent.label || agent.flow?.title || '').trim().toLowerCase();
  return name === 'goal discovery';
}

// Operational spec appended to the Goal Discovery agent's primer. The terse
// FlowDoc steps can't carry the full behaviour, and — crucially — the panel only
// renders the discovery tabs when the agent emits a fenced ```json``` block of
// the exact { "alty_discovery": {...} } shape (App.tsx parses it). So the primer
// spells out both the process and the output contract. Kept here (not in the
// stored FlowDoc) so it travels with every invocation.
const DISCOVERY_AGENT_PRIMER = [
  '',
  'You are the Goal Discovery agent — a customized product-discovery flow (in the spirit of BMAD) for a company executive who describes a goal as a stream of consciousness. A goal can be a major new application feature, a one-off bug, a new customer setup, or anything in between.',
  '',
  'CONTEXT YOU ALREADY HAVE:',
  '- The online app is OneSheet: a highly configurable data / document-management web app (customers, Documents and DocumentTypes, DataRows, Persons, Articles; document edit pages, list/search pages, and config pages). Its code lives under /opt/online21* (ASP.NET MVC — C#, Razor views, JS). The Alt-Y panel you run inside is a separate React app at /opt/alty-app.',
  '- You are already on a OneSheet page; the page context (URL, customer, document, selection) is provided to you. Use it.',
  '',
  'STEP 1 — GATHER ONLY THE CONTEXT YOU NEED. Often this means looking at related work or the codebase; sometimes you need nothing more. Do NOT over-gather — that just slows the first response. If the goal clearly maps to an existing project, find it; otherwise skip it. You may use your page tools, file/search tools, web search, and the memory store. Keep this fast and proportionate to the goal.',
  '',
  'STEP 2 — REPHRASE. Restate the goal clearly and in an organized way (no new requirements). This is the first thing the user sees.',
  '',
  'STEP 3 — PRODUCE FOUR PIECES. Keep each one focused on only the MOST important points so the user is not overwhelmed:',
  '  (a) goalGaps — fill in the most important gaps in the goal: things the executive almost certainly intended but did not spell out. A few high-value items, not an exhaustive list.',
  '  (b) questions — the most important clarifying questions only (aim for 3–6). Skip anything you can reasonably infer.',
  '  (c) userStory — a brief, concrete step-by-step user story, e.g. "1. User opens the Task document edit page. 2. They click the new “Export” button. 3. A dialog opens and they see the generated file."',
  '  (d) design — design ideas: name the specific OneSheet page(s) where the new UI would live and describe, step by step, the new elements and how they would look and behave there. Be concrete about placement.',
  '',
  'If the request is genuinely simple (e.g. a small bug fix), you may fill in only "rephrase" and set "simple": true — skip the heavier pieces.',
  '',
  'OUTPUT CONTRACT (this is what renders the tabs on the right — without it nothing shows): end your reply with a SINGLE fenced ```json code block of EXACTLY this shape:',
  '```json',
  '{ "alty_discovery": {',
  '  "title": "Short title for this goal",',
  '  "rephrase": "Clear, organized restatement (markdown ok).",',
  '  "goalGaps": "The most important filled-in gaps (markdown ok).",',
  '  "questions": ["Most important question 1", "Most important question 2"],',
  '  "userStory": "1. Step one\\n2. Step two\\n3. Step three",',
  '  "design": "Design ideas + which page(s) the UI lives on (markdown ok).",',
  '  "simple": false',
  '} }',
  '```',
  'Keep your normal chat reply to ONE short sentence (e.g. "Here is your discovery brief — open the tabs on the right."); put everything else inside the JSON. Use markdown inside the string fields. When the user asks to change ANY piece, re-emit the WHOLE updated alty_discovery JSON so every tab stays in sync.',
].join('\n');

// Operational spec appended to the apartment finder's primer. The FlowDoc steps
// alone are too terse to drive a real search, and — crucially — the panel only
// persists apartments the agent emits as a fenced ```json``` block of this exact
// shape (App.tsx parses `{ "alty_apartments": [...] }` from the reply and POSTs
// each to /api/alty/apartments, which dedupes). So the primer has to spell out
// both the search criteria and the output contract. Kept here (not in the stored
// FlowDoc) so it travels with every invocation regardless of the saved diagram.
const APARTMENT_AGENT_PRIMER = [
  '',
  'You are searching the Dutch (Netherlands) rental market for a couple and saving verified result cards. Apply ALL of these criteria — be thorough and accurate on every point:',
  '- One-bedroom apartment, for a couple.',
  '- At most 10 minutes from the nearest train station by walking, bus, OR tram.',
  '- Less than 1 hour total travel time to Schiphol Airport.',
  '- Monthly rent between €1600 and €2050.',
  '- Permanent / indefinite lease (not temporary or short-stay).',
  '- Unfurnished, but NOT a bare shell ("casco"). "Gestoffeerd"/"kaal" is fine; "casco" is not.',
  '- Posted within the LAST 12 HOURS on the source site. Skip anything older.',
  '',
  'Finding the ORIGINAL source (important): portals like Pararius are often reposts, and the listing frequently says to find it elsewhere. Search the individual rental-agent (makelaar) websites FIRST — treat them as higher priority than Pararius and other repost portals. For every candidate, trace it back to the actual origin listing and use that origin URL as the canonical source. If you genuinely cannot find the origin, keep the repost but set "originFound": false and say the origin is unknown. Use web search / fetch (and Playwright via the shell if a page needs it) to do this.',
  '',
  'For each apartment that passes ALL criteria, capture: a Google Maps link to the address, its photos, the city, the address, the date it was posted on the source site, and — be thorough here — the best ways to contact the agent or owner (direct agent listing page, phone, email, contact form, WhatsApp, office page) plus a note on which path is most reliable.',
  '',
  'When you have your results, end your reply with a single fenced ```json code block of EXACTLY this shape (this is what saves the cards — without it nothing is saved):',
  '```json',
  '{ "alty_apartments": [',
  '  {',
  '    "title": "1-bed apartment · <city>",',
  '    "city": "Haarlem",',
  '    "address": "Full street address",',
  '    "mapsUrl": "https://www.google.com/maps/search/?api=1&query=<url-encoded address>",',
  '    "price": 1850,',
  '    "postedAt": "2026-05-24 09:30",',
  '    "postedTs": 1748072000,',
  '    "sourceUrl": "https://<original-agent-site>/listing/...",',
  '    "sourceName": "VBO Makelaars Example",',
  '    "originFound": true,',
  '    "isRepost": false,',
  '    "photos": ["https://...jpg", "https://...jpg"],',
  '    "contact": { "agentName": "...", "agentPage": "https://...", "phone": "+31...", "email": "...", "form": "https://...", "whatsapp": "+31...", "officePage": "https://...", "notes": "Phone is fastest; email replies within a day." },',
  '    "details": "Full description, size, floor, energy label, availability date, lease terms…",',
  '    "travel": "8 min walk to Haarlem station · ~38 min to Schiphol"',
  '  }',
  '] }',
  '```',
  'Set "postedTs" to the source posting time in epoch seconds when you can (the cards sort newest-first by it). Include only apartments you actually verified. If none qualify right now, say so plainly and emit an empty array. The cards persist per OneSheet customer; the user can open them any time with the buildings toggle in the panel header, and clicking a card opens its photo gallery and full details.',
].join('\n');

// Operational spec appended to the Customized BMad Agent Flow primer. The
// FlowDoc steps alone are too terse to drive the real workflow, and — crucially
// — the panel only renders the right-side tabs from a fenced ```json``` block of
// this exact shape (App.tsx parses `{ "bmad_workflow": {...} }` from the reply).
// So the primer spells out both the behaviour and the output contract. Kept
// here (not in the stored FlowDoc) so it travels with every invocation
// regardless of the saved diagram. See docs/user-agents/customized-bmad-agent-flow.md.
const BMAD_WORKFLOW_PRIMER = [
  '',
  'You are the **Customized BMad Agent Flow** — a BMad-style product-intake agent for a company executive (a OneSheet SuperAdmin). The user gives you a free-form, stream-of-consciousness goal: a major feature, a one-off bug, a new customer setup, or anything else. Your job is to turn it into review-ready BMad artifacts and a dependency-aware build plan that the user can launch with Build.',
  '',
  'Work in this order. Move fast and keep each piece TIGHT — surface only the most important points so the executive is not overwhelmed.',
  '',
  'IMAGE REQUEST OVERRIDE: If the user directly asks you to create, edit, mock up, or show an image, call the `openai-images` MCP tool `generate_bmad_mockup` before replying. Do not read local imagegen skill docs, say you will generate an image, or substitute text-only design notes. Include the tool\'s returned Markdown snippet in chat so the image appears inline. If this is part of a BMAD design, also put the returned `url` into `design.pages[].mockup`.',
  '',
  'KNOWN DOCUMENTATION SOURCE: IDsys Online help lives at https://scribehow.com/page/IDsys_Online_kasutusjuhend__pqv8NwR-RwaB_KyapFJqNA — the same page opened by the in-app Help/question-mark button. When docs matter, inspect that help hub and its linked pages, views, screenshots, and assets as IDsys documentation.',
  '',
  '1. GATHER BOUNDED CONTEXT (only what you need). You already know the landscape: the online OneSheet app lives in /opt/online21* (C#/Razor/JS) and the Alt-Y component in /opt/alty-app + /opt/onesheet-agent-y. Relevant sources can include internal company Projects/Development Tasks (idsys_list_projects / idsys_get_project / idsys_list_tasks), ClickUp tickets/comments/attachments when a ClickUp id or URL is available, the visual-context screenshot library, IDsys documentation/help pages, current page context, code/docs, memory, and prior chat/subchat artifacts. Use projects only as context and as an optional existing project link if you are confident; do not create or edit projects. Only then read the specific code/docs you actually need. It is fine to decide you need NO extra context — say so in `context.notes`, and still emit `sources` explaining which sources were used or skipped.',
  '2. REPHRASE the goal in a clearer, better-organized way and emit it IMMEDIATELY (in the `rephrase` field) so it shows in the right-side tabs right away, before the heavier work.',
  '3. Decide the path. A simple bug / narrow task → set "kind":"bug": do the rephrase, then propose ONE development task and skip the larger drafting pieces unless a major architecture decision genuinely matters. A larger goal → set "kind":"feature" and draft these pieces:',
  '   - SOURCES: a short trace of the existing information you used or intentionally skipped. Include internal projects/tasks, ClickUp, screenshot library, IDsys documentation/help pages, code/docs, page context, memory, or other sources only when relevant. For each source, say what it is and how it influenced the workflow.',
  '   - GAPS: the most important missing decisions in the goal (only the few that matter — do not overcomplicate early).',
  '   - QUESTIONS: the most important clarification questions (only the few that matter). For each question, include your best current answer guess and a 0-100 confidence score so the user can correct you instead of starting from blank.',
  '   - USER STORY: a brief, concrete step-by-step story as user actions + visible outcomes, e.g. "1. User opens the Documents/Edit page", "2. They click the new Summarize button", "3. A summary panel appears with the generated text".',
  '   - DESIGN: find the page(s) where the UI will live, check the visual-context screenshot library named in the workspace instructions before taking new screenshots, describe exactly where the new elements go, and — when it helps — use an existing or fresh screenshot plus the `openai-images` MCP tool `generate_bmad_mockup` to render a step-by-step mockup with ChatGPT image 2.0 (`gpt-image-2`), which is extremely good and detailed. Make UI designs polished and visually appealing. The tool accepts screenshots from the current workspace or the curated visual library root. Put the returned `url` in `design.pages[].mockup` and include the returned markdown in chat when the user needs to see the image immediately. Align `design.pages[]` by index with `userStory[]`; the panel renders each story step as a subtab in the Design tab, so use one design page entry per story step when the design differs by step. If image generation is unavailable, describe the mockup precisely instead. The design must MATCH the user story; if the user later edits the story (or any piece), update ALL affected pieces — design included — and re-emit the whole block.',
  '     For shell screenshots, prefer the installed Playwright CLI: `playwright screenshot --browser chromium --wait-for-timeout 3000 "<url>" /tmp/<descriptive-name>.png`. If you use `npx playwright` and see a missing-browser error, run `npx --yes playwright install chromium` once, then retry the screenshot.',
  '   - MAJOR ARCHITECTURAL DECISIONS: only the few build-shaping decisions that materially affect implementation, data flow, ownership, persistence, integrations, or rollback risk. Do NOT clutter this with minor styling, naming, copy, or local code-organization questions. Each item must include the decision question, your recommended answer, and a 0-100 confidence score.',
  '   You may launch parallel sub-agents (the Task tool) to draft these simultaneously, and you may use the installed bmad-* skills (bmad-product-brief, bmad-create-prd, bmad-create-epics-and-stories, …) wherever they help.',
  '4. BUILD READY. After you present the pieces, set "status":"awaiting_build" and tell the user to review the tabs and press Build. The right-side view has a Build button; it does NOT send a chat confirmation. It asks the broker to launch implementation subchats.',
  '5. BUILD PLAN. Fill `tasks` with the implementation plan the broker should execute. For larger work, include one Epic item (`title` starts with `Epic:`) plus Story items. The Epic is only a visual/grouping parent; do NOT put the Epic title in `dependsOn`. Use `tasks[].dependsOn` only for real Story-to-Story prerequisites that block implementation order. Include all context a downstream implementation agent needs (rephrase, sources and how they were used, user story, relevant gaps, question answer guesses with confidence, major architecture decisions with confidence, design notes) in each task description. Do not create internal Development Task records and do not call idsys_create_task/project tools for this Build flow.',
  '',
  'OUTPUT CONTRACT — every turn, after your short chat reply, emit the COMPLETE current state as ONE fenced ```json code block of EXACTLY this shape (re-emit the whole object each turn, like a living document; omit fields you have nothing for). The block is what drives the right-side tabs — without it the tabs do not update:',
  '```json',
  '{ "bmad_workflow": {',
  '  "kind": "feature",',
  '  "status": "awaiting_build",',
  '  "goal": "the executive\'s original goal, briefly",',
  '  "rephrase": "A clear, reorganized restatement of what they want.",',
  '  "sources": [',
  '    { "type": "internal_project", "name": "Existing project title", "id": 123, "description": "What this source contains.", "usedFor": "How it influenced the workflow.", "confidence": 90 },',
  '    { "type": "clickup", "name": "ClickUp task/comment/attachment", "url": "https://app.clickup.com/...", "description": "Existing request or evidence found there.", "usedFor": "What requirement or constraint it informed.", "confidence": 80 },',
  '    { "type": "idsys_documentation", "name": "IDsys Online help page", "url": "https://scribehow.com/page/IDsys_Online_kasutusjuhend__pqv8NwR-RwaB_KyapFJqNA", "description": "Relevant docs/help view/assets.", "usedFor": "How the docs shaped the recommendation.", "confidence": 75 }',
  '  ],',
  '  "gaps": ["Most important open decision 1", "..."],',
  '  "questions": [',
  '    { "question": "Most important clarification question 1?", "guess": "Best current answer from available context.", "confidence": 70 }',
  '  ],',
  '  "userStory": ["1. User goes to …", "2. They click …", "3. They see …"],',
  '  "design": {',
  '    "summary": "Where the UI lives and the overall approach.",',
  '    "pages": [ { "name": "Step 1 - Documents/Edit", "url": "https://…", "changes": "Add a Summarize button next to Save; clicking opens a right-side summary panel.", "mockup": "https://…optional-image" } ],',
  '    "notes": "Anything else worth knowing about the design."',
  '  },',
  '  "architectureDecisions": [',
  '    { "question": "Which major implementation boundary should own this capability?", "answer": "Recommended answer with rationale.", "confidence": 85 }',
  '  ],',
  '  "context": { "relatedProjects": ["Existing project title", "…"], "notes": "What you gathered, or why none was needed." },',
  '  "project": { "match": "existing", "title": "Optional existing project title", "id": 123 },',
  '  "tasks": [',
  '    { "title": "Epic: Feature or workstream title", "description": "Overall delivery context.", "status": "open", "id": 455 },',
  '    { "title": "Story: First implementation task", "description": "Full context for the implementer.", "status": "open", "id": 456 },',
  '    { "title": "Story: Later implementation task", "description": "Full context for the implementer.", "dependsOn": ["Story: First implementation task"], "status": "open", "id": 457 }',
  '  ]',
  '} }',
  '```',
  'Keep the chat reply itself short (a sentence or two pointing at the tabs) — the JSON block carries the detail and the panel hides it from the bubble. Include `tasks` when they are proposed; the broker uses them as the Build plan. Include `project` only for an existing project you identified; never invent a new project record.',
].join('\n');

// Deep-equal for FlowDoc — used to decide whether the chat's latest emitted
// diagram has diverged from the pin's stored implementation. Pure JSON, so a
// JSON-roundtrip stringify is the simplest reliable comparator. The written
// `spec` brief is excluded: "modified from implementation" / improve "no
// change" are about the DIAGRAM, and the designer re-emits a fresh brief every
// turn (and built-in self-portraits carry none), so comparing it would flag a
// change on every turn.
export function flowEquals(a: FlowDoc | null, b: FlowDoc | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  try {
    const { spec: _a, ...da } = a;
    const { spec: _b, ...db } = b;
    return JSON.stringify(da) === JSON.stringify(db);
  } catch {
    return false;
  }
}

// User agents now live on the broker (owner-scoped) instead of localStorage —
// so a pin minted on one browser/device shows up everywhere the same user
// signs in. The legacy `userAgentsKey` is still read once on startup by the
// `migrateLegacyUserAgents` helper below, then removed.

function isPinnedAgent(value: unknown): value is PinnedAgent {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<PinnedAgent>;
  return typeof v.id === 'string'
    && typeof v.label === 'string'
    && !!v.flow
    && Array.isArray(v.flow.steps);
}

export async function listUserAgents(): Promise<PinnedAgent[]> {
  const list = await listAltYAgents();
  return Array.isArray(list) ? list.filter(isPinnedAgent) : [];
}

// Parse "every N (s|m|h|seconds?|minutes?|hours?)" from a trigger step text.
// Returns seconds, or null if no match.
function parseTriggerIntervalSeconds(text: string): number | null {
  const m = text.match(/every\s+(\d+)\s*(s(?:ec(?:ond)?s?)?|m(?:in(?:ute)?s?)?|h(?:our?s?)?)\b/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = m[2].toLowerCase();
  if (unit.startsWith('h')) return n * 3600;
  if (unit.startsWith('m')) return n * 60;
  return n; // seconds
}

export async function createUserAgent(
  flow: FlowDoc,
  creatorRunId?: number | null,
  options: { pinned?: boolean; customerId?: number; baseUrl?: string; tickIntervalSeconds?: number } = {},
): Promise<PinnedAgent> {
  // `pinned` controls sidebar visibility; the picker shows the agent
  // regardless. Defaults to true for callers that don't specify (back-compat
  // with the legacy single-button flow that always pinned).
  const pinned = options.pinned !== false;

  // Belt-and-suspenders: parse the tick interval from the trigger step text
  // if not provided by the caller.
  let tickIntervalSeconds = options.tickIntervalSeconds;
  if (tickIntervalSeconds == null) {
    const trigger = flow.steps?.find((s) => s.kind === 'trigger');
    if (trigger?.text) {
      const parsed = parseTriggerIntervalSeconds(trigger.text);
      if (parsed != null) tickIntervalSeconds = parsed;
    }
  }

  const agent: PinnedAgent = {
    id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: flow.title || 'Untitled agent',
    icon: 'fa-robot',
    mode: 'interact',
    runnable: true,
    flow,
    pinned,
    ...(creatorRunId ? { creatorRunId } : {}),
    ...(options.customerId != null ? { customerId: options.customerId } : {}),
    ...(options.baseUrl ? { baseUrl: options.baseUrl } : {}),
    ...(tickIntervalSeconds != null ? { tickIntervalSeconds } : {}),
  };
  return createAltYAgent(agent);
}

export async function updateUserAgent(id: string, patch: Partial<PinnedAgent>): Promise<PinnedAgent> {
  return updateAltYAgent(id, patch);
}

export async function deleteUserAgent(id: string): Promise<void> {
  await deleteAltYAgent(id);
}

// One-time migration: pre-server-side builds stored user agents in
// `localStorage.altY.userAgents.v1`. On first list-from-server after the
// switch we POST each surviving entry up to the broker (idempotent upsert)
// then clear the localStorage key so subsequent reloads use the server only.
export async function migrateLegacyUserAgents(): Promise<number> {
  let raw: string | null = null;
  try { raw = localStorage.getItem(userAgentsKey); } catch { return 0; }
  if (!raw) return 0;
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { parsed = null; }
  const entries = Array.isArray(parsed) ? parsed.filter(isPinnedAgent) : [];
  if (!entries.length) {
    try { localStorage.removeItem(userAgentsKey); } catch { /* ignore */ }
    return 0;
  }
  let migrated = 0;
  for (const agent of entries) {
    try {
      await createAltYAgent(agent);
      migrated += 1;
    } catch {
      // Leave the localStorage key in place if any single POST fails so the
      // user gets another chance on the next reload — partial loss is worse
      // than retrying the whole batch.
      return migrated;
    }
  }
  try { localStorage.removeItem(userAgentsKey); } catch { /* ignore */ }
  return migrated;
}

function describeStep(step: FlowStep, index: number): string {
  const tag = step.kind === 'trigger' ? 'Trigger'
    : step.kind === 'gate' ? 'Decision'
    : step.kind === 'fork' ? 'Branch'
    : 'Step';
  const chip = step.chip ? ` [${step.chip}]` : '';
  const main = `${index + 1}. ${tag}${chip}: ${step.text}`.trim();
  const extras: string[] = [];
  if (step.kind === 'fork') {
    if (step.leftLabel) extras.push(`   - ${step.leftLabel}: continue main path`);
    if (step.rightLabel) extras.push(`   - ${step.rightLabel}: continue main path`);
    if (step.side) extras.push(`   - Side branch: ${step.side.text}`);
  }
  if (step.loop) extras.push(`   - Loop: ${step.loop}`);
  return [main, ...extras].join('\n');
}

// Separator between the agent primer and the actual user message in the
// payload sent to the broker. The broker echoes the full text back as the
// `_user` event, so the chat hook strips everything up to (and including)
// this marker before rendering — the user bubble shows only what they typed.
export const AGENT_PRIMER_SEPARATOR = '\n\n— Begin user request —\n';

// Primer used when the user opens an Agent Factory edit chat for a built-in
// pin. Frames the conversation as "iterate ON this diagram", with the pin's
// current FlowDoc embedded as the starting point. The agent then behaves
// exactly like normal agent-factory mode — emits a fenced ```json``` block
// each turn with the full updated diagram.
export function formatPinEditPrimer(agent: PinnedAgent): string {
  const flow = JSON.stringify(agent.flow, null, 2);
  return [
    `You are now editing the **${agent.label}** pinned agent itself.`,
    '',
    'Below is the diagram that currently represents this agent\'s implementation.',
    'The user wants to modify this existing agent, not create a separate new one. Listen to their changes, then re-emit the WHOLE updated diagram as a fenced ```json``` block. Use the same FlowDoc shape shown below unless the user explicitly asks to convert it. Keep the diagram minimal: only add nodes / fields the user explicitly asked for.',
    '',
    'Current diagram:',
    '```json',
    flow,
    '```',
  ].join('\n');
}

// Build the system-style instructions Alt-Y reads when the user invokes a
// pinned agent. The wrapper makes the LLM treat the flow as its current job
// and the user's typed message as the trigger / parameters for that job.
export function formatFlowAsPrimer(agent: PinnedAgent): string {
  const { flow } = agent;
  const lines: string[] = [];
  lines.push(`You are now operating as the "${flow.title || agent.label}" pinned agent.`);
  if (flow.description) lines.push('', flow.description);
  if (flow.steps?.length) {
    lines.push('', 'Flow:');
    flow.steps.forEach((step, index) => lines.push(describeStep(step, index)));
  }
  lines.push('', 'Use the available page tools to actually execute this flow against the page the user is on. The user message below is the trigger / parameters for this run.');
  lines.push('When existing context would help, check it proportionately: current page context, internal IDsys projects/tasks, ClickUp links or task ids provided by the user/context, the visual-context screenshot library, and the IDsys Online help hub opened by the in-app Help button: https://scribehow.com/page/IDsys_Online_kasutusjuhend__pqv8NwR-RwaB_KyapFJqNA.');
  // The apartment finder needs its full search criteria + the JSON output
  // contract that lets the panel persist the cards it finds; the terse FlowDoc
  // steps can't carry that, so append the operational spec here.
  if (isApartmentAgent(agent)) lines.push(APARTMENT_AGENT_PRIMER);
  // The Customized BMad Agent Flow needs its full intake spec + the JSON output
  // contract that drives the right-side workflow tabs; the terse FlowDoc steps
  // can't carry that, so append the operational spec here.
  if (isBmadWorkflowAgent(agent)) lines.push(BMAD_WORKFLOW_PRIMER);
  // The Goal Discovery agent needs its full process + the JSON output contract
  // that drives the right-pane tabs; the terse FlowDoc steps can't carry that.
  if (isDiscoveryAgent(agent)) lines.push(DISCOVERY_AGENT_PRIMER);
  return lines.join('\n');
}
